import { Language } from '@/types';

export interface Translations {
  // App title and branding
  title: string;
  brandName: string;
  subtitle: string;
  tagline: string;

  // Navigation
  offerServices: string;
  requestHelp: string;
  myExchanges: string;
  incomingRequests: string;
  myProfile: string;
  adminDashboard: string;

  // Authentication
  signIn: string;
  signUp: string;
  signOut: string;
  createAccount: string;
  email: string;
  password: string;
  displayName: string;
  postalCode: string;
  postalCodeHelper: string;
  dontHaveAccount: string;
  alreadyHaveAccount: string;

  // Service offering
  selectServices: string;
  preferredPayment: string;
  communityPoints: string;
  serviceExchange: string;
  eitherOption: string;
  cad: string;
  submit: string;
  offerSent: string;

  // Availability
  availability: string;
  availabilityDescription: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
  morning: string;
  afternoon: string;
  evening: string;

  // Service requesting
  balance: string;
  status: string;
  offeredBy: string;
  available: string;
  requestBtn: string;
  requestSent: string;

  // Service management
  with: string;
  time: string;
  cancel: string;
  accept: string;
  decline: string;

  // Chat
  chat: string;
  chatTitle: string;
  typeMessage: string;
  sendMessage: string;
  closeChat: string;

  // User profile
  profile: string;
  editProfile: string;
  displayName: string;
  bio: string;
  phoneNumber: string;
  uploadPhoto: string;
  saveChanges: string;

  // Ratings
  rateExchange: string;
  rating: string;
  comment: string;
  submitRating: string;

  // Status levels
  status_new_neighbor: string;
  status_helping_hand: string;
  status_local_connector: string;
  status_community_pillar: string;

  // Search and filter
  search: string;
  searchPlaceholder: string;
  filterByCategory: string;
  filterByPayment: string;
  allCategories: string;
  allPayments: string;
  clearFilters: string;

  // Admin
  totalUsers: string;
  totalExchanges: string;
  totalServices: string;
  activeUsers: string;
  recentActivity: string;

  // Messages and errors
  loading: string;
  error: string;
  success: string;
  noResults: string;
  noServices: string;
  noExchanges: string;
  noMessages: string;

  // Legal
  termsLabel: string;
  privacyLabel: string;
  consentPrefix: string;
  andWord: string;
  builtFor: string;
  rights: string;

  // Common actions
  cancel: string;
  confirm: string;
  delete: string;
  edit: string;
  save: string;
  back: string;
  next: string;
}

export const translations: Record<Language, Translations> = {
  en: {
    // App title and branding
    title: 'MutuAid',
    brandName: 'Mutuaid',
    subtitle: 'Community Service Exchange Platform',
    tagline: 'Community support, made practical',

    // Navigation
    offerServices: 'Offer Services',
    requestHelp: 'Request Help',
    myExchanges: 'Scheduled Services',
    incomingRequests: 'Requests Received',
    myProfile: 'My Profile',
    adminDashboard: 'Admin Dashboard',

    // Authentication
    signIn: 'Sign In',
    signUp: 'Sign Up',
    signOut: 'Sign Out',
    createAccount: 'Create an Account',
    email: 'Email',
    password: 'Password',
    displayName: 'Display Name',
    postalCode: 'Postal Code (First 3 digits)',
    postalCodeHelper: 'e.g., H2X (for approximate location)',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',

    // Service offering
    selectServices: 'Select the services you can provide to your community.',
    preferredPayment: 'Preferred Payment',
    communityPoints: 'Community Points',
    serviceExchange: 'Service Exchange',
    eitherOption: 'No preference',
    cad: 'Cash',
    submit: 'Submit Offerings',
    offerSent: 'Your offering has been submitted!',

    // Availability
    availability: 'Availability',
    availabilityDescription: 'Select when you are available to help',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',

    // Service requesting
    balance: 'Balance',
    status: 'Status',
    offeredBy: 'Offered by',
    available: 'Available',
    requestBtn: 'Request Help',
    requestSent: 'Request sent!',

    // Service management
    with: 'With',
    time: 'Time',
    cancel: 'Cancel Exchange',
    accept: 'Accept',
    decline: 'Decline',

    // Chat
    chat: 'Message',
    chatTitle: 'Chat',
    typeMessage: 'Type a message...',
    sendMessage: 'Send',
    closeChat: 'Close',

    // User profile
    profile: 'Profile',
    editProfile: 'Edit Profile',
    displayName: 'Display Name',
    bio: 'Bio',
    phoneNumber: 'Phone Number',
    uploadPhoto: 'Upload Photo',
    saveChanges: 'Save Changes',

    // Ratings
    rateExchange: 'Rate this exchange',
    rating: 'Rating',
    comment: 'Comment (optional)',
    submitRating: 'Submit Rating',

    // Status levels
    status_new_neighbor: '🌱 New Neighbor',
    status_helping_hand: '🤝 Helping Hand',
    status_local_connector: '💬 Local Connector',
    status_community_pillar: '🌟 Community Pillar',

    // Search and filter
    search: 'Search',
    searchPlaceholder: 'Search services...',
    filterByCategory: 'Filter by category',
    filterByPayment: 'Filter by payment',
    allCategories: 'All Categories',
    allPayments: 'All Payments',
    clearFilters: 'Clear Filters',

    // Admin
    totalUsers: 'Total Users',
    totalExchanges: 'Total Exchanges',
    totalServices: 'Total Services',
    activeUsers: 'Active Users',
    recentActivity: 'Recent Activity',

    // Messages and errors
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Success!',
    noResults: 'No results found',
    noServices: 'No services available',
    noExchanges: 'No exchanges yet',
    noMessages: 'No messages yet',

    // Legal
    termsLabel: 'Terms',
    privacyLabel: 'Privacy Policy',
    consentPrefix: 'By continuing, you agree to our',
    andWord: 'and',
    builtFor: 'Built for local communities',
    rights: 'All rights reserved.',

    // Common actions
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    save: 'Save',
    back: 'Back',
    next: 'Next',
  },
  fr: {
    // App title and branding
    title: 'MutuAid',
    brandName: 'MutuAid',
    subtitle: "Plateforme d'Échange de Services Communautaires",
    tagline: 'Le soutien communautaire, concrètement',

    // Navigation
    offerServices: 'Offrir des Services',
    requestHelp: "Demander de l'Aide",
    myExchanges: 'Mes Échanges',
    incomingRequests: 'Demandes Reçues',
    myProfile: 'Mon Profil',
    adminDashboard: 'Tableau de bord Admin',

    // Authentication
    signIn: 'Connexion',
    signUp: "S'inscrire",
    signOut: 'Déconnexion',
    createAccount: 'Créer un compte',
    email: 'Courriel',
    password: 'Mot de passe',
    displayName: "Nom d'affichage",
    postalCode: 'Code postal (3 premiers chiffres)',
    postalCodeHelper: 'ex: H2X (pour localisation approximative)',
    dontHaveAccount: "Vous n'avez pas de compte ?",
    alreadyHaveAccount: 'Vous avez déjà un compte ?',

    // Service offering
    selectServices: 'Sélectionnez les services que vous pouvez fournir à votre communauté.',
    preferredPayment: 'Mode de paiement préféré',
    communityPoints: 'Points Communautaires',
    serviceExchange: 'Échange de services',
    eitherOption: 'Aucune préférence',
    cad: 'Argent comptant',
    submit: 'Soumettre des offres',
    offerSent: 'Votre offre a été soumise!',

    // Availability
    availability: 'Disponibilités',
    availabilityDescription: 'Sélectionnez quand vous êtes disponible pour aider',
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
    morning: 'Matin',
    afternoon: 'Après-midi',
    evening: 'Soir',

    // Service requesting
    balance: 'Solde',
    status: 'Statut',
    offeredBy: 'Offert par',
    available: 'Disponible',
    requestBtn: "Demander de l'aide",
    requestSent: 'Demande envoyée',

    // Service management
    with: 'Avec',
    time: 'Heure',
    cancel: "Annuler l'échange",
    accept: 'Accepter',
    decline: 'Refuser',

    // Chat
    chat: 'Envoyer un message',
    chatTitle: 'Discussion',
    typeMessage: 'Écrire un message...',
    sendMessage: 'Envoyer',
    closeChat: 'Fermer',

    // User profile
    profile: 'Profil',
    editProfile: 'Modifier le profil',
    displayName: 'Nom affiché',
    bio: 'Biographie',
    phoneNumber: 'Numéro de téléphone',
    uploadPhoto: 'Télécharger une photo',
    saveChanges: 'Enregistrer les modifications',

    // Ratings
    rateExchange: 'Évaluer cet échange',
    rating: 'Évaluation',
    comment: 'Commentaire (optionnel)',
    submitRating: "Soumettre l'évaluation",

    // Status levels
    status_new_neighbor: '🌱 Nouveau voisin',
    status_helping_hand: '🤝 Coup de main',
    status_local_connector: '💬 Connecteur local',
    status_community_pillar: '🌟 Pilier de la communauté',

    // Search and filter
    search: 'Rechercher',
    searchPlaceholder: 'Rechercher des services...',
    filterByCategory: 'Filtrer par catégorie',
    filterByPayment: 'Filtrer par paiement',
    allCategories: 'Toutes les catégories',
    allPayments: 'Tous les paiements',
    clearFilters: 'Effacer les filtres',

    // Admin
    totalUsers: 'Utilisateurs totaux',
    totalExchanges: 'Échanges totaux',
    totalServices: 'Services totaux',
    activeUsers: 'Utilisateurs actifs',
    recentActivity: 'Activité récente',

    // Messages and errors
    loading: 'Chargement...',
    error: "Une erreur s'est produite",
    success: 'Succès!',
    noResults: 'Aucun résultat trouvé',
    noServices: 'Aucun service disponible',
    noExchanges: 'Aucun échange pour le moment',
    noMessages: 'Aucun message pour le moment',

    // Legal
    termsLabel: 'Conditions',
    privacyLabel: 'Politique de confidentialité',
    consentPrefix: 'En continuant, vous acceptez nos',
    andWord: 'et',
    builtFor: 'Conçu pour les communautés locales',
    rights: 'Tous droits réservés.',

    // Common actions
    confirm: 'Confirmer',
    delete: 'Supprimer',
    edit: 'Modifier',
    save: 'Enregistrer',
    back: 'Retour',
    next: 'Suivant',
  },
};

export const getTranslation = (language: Language): Translations => {
  return translations[language];
};
